<template>
  <div>
    <v-card class="card-shadow border-radius-xl">
      <v-card-title>
        <span class="font-weight-bold text-h5 text-typo">出勤紀錄表</span>
      </v-card-title>
      <v-card-subtitle>
        <v-row>
          <v-col cols="12" md="2">
            <v-select
              v-model="selectedYear"
              :items="yearOptions"
              hide-details
              dense
              outlined
              class="
                mt-0
                input-style
                font-size-input
                text-light-input
                placeholder-light
                input-icon
              "
              label="年份"
              @change="onFilterChange"
            ></v-select>
          </v-col>
          <v-col cols="12" md="1">
            <v-select
              v-model="selectedMonth"
              :items="monthOptions"
              hide-details
              dense
              outlined
              class="
                mt-0
                input-style
                font-size-input
                text-light-input
                placeholder-light
                input-icon
              "
              label="月份"
              @change="onFilterChange"
            ></v-select>
          </v-col>

          <v-spacer></v-spacer>

          <v-col cols="12" md="3" class="text-right">
            <v-btn
              @click="exportMonthlySummary"
              elevation="0"
              :ripple="false"
              height="43"
              :loading="exporting"
              :disabled="exportDisabled"
              class="
                font-weight-bold
                text-capitalize
                btn-ls btn-primary
                bg-gradient-primary
                py-3
                px-6
              "
            >
              <v-icon size="18" class="me-1">mdi-download</v-icon>
              匯出Excel
            </v-btn>
          </v-col>
        </v-row>
      </v-card-subtitle>
      <v-card-text class="px-0 py-0">
        <v-data-table
          :headers="headers"
          :items="salaries"
          :loading="loading"
          :server-items-length="totalItems"
          class="table"
          hide-default-footer
          @page-count="pageCount = $event"
          disable-pagination
          :items-per-page="itemsPerPage"
          mobile-breakpoint="0"
          :item-class="rowClasses"
        >
          <template v-slot:item.user.name="{ item }">
            <div class="d-flex align-center">
              <span
                class="
                  text-sm
                  font-weight-normal
                  text-body text-primary
                  cursor-pointer
                "
                @click.stop="goToUserProfile(item.user ? item.user.id : null)"
              >
                {{ item.user.name }}
                <span
                  v-if="isNewEmployee(item)"
                  class="text-xs text-danger font-weight-bold"
                  >(新)</span
                >
              </span>
            </div>
          </template>

          <template v-slot:item.job_title="{ item }">
            <div class="d-flex align-center">
              <span class="text-sm font-weight-normal text-body">
                {{ item.user ? item.user.job_title : "-" }}
              </span>
            </div>
          </template>

          <template v-slot:item.department="{ item }">
            <div class="d-flex align-center">
              <span class="text-sm font-weight-normal text-body">
                {{ item.user ? item.user.department : "-" }}
              </span>
            </div>
          </template>

          <template v-slot:item.expected_sign_date="{ item }">
            <div class="d-flex align-center">
              <span class="text-sm font-weight-normal text-body">
                {{
                  item.user && item.user.employeeProfile
                    ? formatROCDate(
                        item.user.employeeProfile.expected_sign_date
                      )
                    : "-"
                }}
              </span>
            </div>
          </template>

          <template v-slot:item.personal_leave="{ item }">
            <div v-if="editingId === item.id">
              <v-text-field
                v-model.number="editedItem.personal_leave"
                type="number"
                min="0"
                dense
                hide-details
                class="edit-input"
              ></v-text-field>
            </div>
            <div v-else class="d-flex align-center">
              <span class="text-sm font-weight-normal text-body">
                {{ item.personal_leave || 0 }}
              </span>
            </div>
          </template>

          <template v-slot:item.sick_leave="{ item }">
            <div v-if="editingId === item.id">
              <v-text-field
                v-model.number="editedItem.sick_leave"
                type="number"
                min="0"
                dense
                hide-details
                class="edit-input"
              ></v-text-field>
            </div>
            <div v-else class="d-flex align-center">
              <span class="text-sm font-weight-normal text-body">
                {{ item.sick_leave || 0 }}
              </span>
            </div>
          </template>

          <template v-slot:item.annual_leave="{ item }">
            <div v-if="editingId === item.id">
              <v-text-field
                v-model.number="editedItem.annual_leave"
                type="number"
                min="0"
                dense
                hide-details
                class="edit-input"
              ></v-text-field>
            </div>
            <div v-else class="d-flex align-center">
              <span class="text-sm font-weight-normal text-body">
                {{ item.annual_leave || 0 }}
              </span>
            </div>
          </template>

          <template v-slot:item.menstrual_leave="{ item }">
            <div v-if="editingId === item.id">
              <v-text-field
                v-model.number="editedItem.menstrual_leave"
                type="number"
                min="0"
                dense
                hide-details
                class="edit-input"
              ></v-text-field>
            </div>
            <div v-else class="d-flex align-center">
              <span class="text-sm font-weight-normal text-body">
                {{ item.menstrual_leave || 0 }}
              </span>
            </div>
          </template>

          <template v-slot:item.family_care_leave="{ item }">
            <div v-if="editingId === item.id">
              <v-text-field
                v-model.number="editedItem.family_care_leave"
                type="number"
                min="0"
                dense
                hide-details
                class="edit-input"
              ></v-text-field>
            </div>
            <div v-else class="d-flex align-center">
              <span class="text-sm font-weight-normal text-body">
                {{ item.family_care_leave || 0 }}
              </span>
            </div>
          </template>

          <template v-slot:item.late_info="{ item }">
            <div v-if="editingId === item.id">
              <div class="d-flex align-center">
                <v-text-field
                  v-model.number="editedItem.late_count"
                  type="number"
                  min="0"
                  dense
                  hide-details
                  class="edit-input mr-1"
                  style="max-width: 50px"
                  label="次"
                ></v-text-field>
                <v-text-field
                  v-model.number="editedItem.late"
                  type="number"
                  min="0"
                  dense
                  hide-details
                  class="edit-input"
                  style="max-width: 50px"
                  label="分"
                ></v-text-field>
              </div>
            </div>
            <div v-else class="d-flex align-center">
              <span class="text-sm font-weight-normal text-body">
                {{ item.late_count || 0 }}次 ({{ item.late || 0 }}分)
              </span>
            </div>
          </template>

          <template v-slot:item.early_info="{ item }">
            <div v-if="editingId === item.id">
              <div class="d-flex align-center">
                <v-text-field
                  v-model.number="editedItem.early_count"
                  type="number"
                  min="0"
                  dense
                  hide-details
                  class="edit-input mr-1"
                  style="max-width: 50px"
                  label="次"
                ></v-text-field>
                <v-text-field
                  v-model.number="editedItem.early"
                  type="number"
                  min="0"
                  dense
                  hide-details
                  class="edit-input"
                  style="max-width: 50px"
                  label="分"
                ></v-text-field>
              </div>
            </div>
            <div v-else class="d-flex align-center">
              <span class="text-sm font-weight-normal text-body">
                {{ item.early_count || 0 }}次 ({{ item.early || 0 }}分)
              </span>
            </div>
          </template>

          <template v-slot:item.absence_info="{ item }">
            <div v-if="editingId === item.id">
              <div class="d-flex align-center">
                <v-text-field
                  v-model.number="editedItem.absence_count"
                  type="number"
                  min="0"
                  dense
                  hide-details
                  class="edit-input mr-1"
                  style="max-width: 50px"
                  label="次"
                ></v-text-field>
                <v-text-field
                  v-model.number="editedItem.absence_minutes"
                  type="number"
                  min="0"
                  dense
                  hide-details
                  class="edit-input"
                  style="max-width: 50px"
                  label="分"
                ></v-text-field>
              </div>
            </div>
            <div v-else class="d-flex align-center">
              <span class="text-sm font-weight-normal text-body">
                {{ item.absence_count || 0 }}次 ({{
                  item.absence_minutes || 0
                }}分)
              </span>
            </div>
          </template>

          <template v-slot:item.actions="{ item }">
            <div v-if="editingId === item.id" class="d-flex align-center">
              <v-btn
                @click="saveEdit(item)"
                icon
                elevation="0"
                :ripple="false"
                height="28"
                min-width="36"
                width="36"
                class="btn-ls me-1 rounded-sm"
                color="success"
                :loading="saving"
              >
                <v-icon size="18">mdi-check</v-icon>
              </v-btn>
              <v-btn
                @click="cancelEdit"
                icon
                elevation="0"
                :ripple="false"
                height="28"
                min-width="36"
                width="36"
                class="btn-ls rounded-sm"
                color="error"
                :disabled="saving"
              >
                <v-icon size="18">mdi-close</v-icon>
              </v-btn>
            </div>
            <div v-else class="d-flex align-center">
              <v-btn
                @click="editItem(item)"
                icon
                elevation="0"
                :ripple="false"
                height="28"
                min-width="36"
                width="36"
                class="btn-ls me-1 rounded-sm"
                color="primary"
              >
                <v-icon size="18">mdi-pencil</v-icon>
              </v-btn>
              <v-btn
                @click="exportMonthlySummary(item.user_id, item.name)"
                icon
                elevation="0"
                :ripple="false"
                height="28"
                min-width="36"
                width="36"
                class="btn-ls rounded-sm"
                color="success"
              >
                <v-icon size="18">mdi-file-excel</v-icon>
              </v-btn>
              <!-- reset -->
              <v-btn
                @click="resetItem(item)"
                icon
                elevation="0"
                :ripple="false"
                height="28"
                min-width="36"
                width="36"
                class="btn-ls rounded-sm"
                color="error"
              >
                <v-icon size="18">mdi-refresh</v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>

        <div id="sentinel-salary-preview" class="d-flex justify-center py-4">
          <v-progress-circular
            v-if="loadingMore"
            indeterminate
            color="#cb0c9f"
            size="24"
          ></v-progress-circular>
          <v-btn
            v-else-if="fetchError"
            color="error"
            small
            outlined
            @click="
              salaries.length === 0 ? fetchSalaries() : fetchSalaries(0, true)
            "
          >
            <v-icon left>mdi-refresh</v-icon>
            重試
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <!-- Progress Dialog -->
    <v-dialog v-model="showProgressDialog" persistent max-width="400">
      <v-card>
        <v-card-text class="text-center py-5">
          <v-progress-circular
            indeterminate
            color="primary"
            size="50"
          ></v-progress-circular>
          <div class="mt-3">{{ progressMessage }}</div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <confirm-dialog
      :visible.sync="confirmDialog.show"
      :title="confirmDialog.title"
      :message="confirmDialog.message"
      :yes-button-text="confirmDialog.confirmText"
      :no-button-text="confirmDialog.noButtonText"
      :yes-callback="confirmDialog.onConfirm"
    />

    <v-snackbar
      v-model="snackbar.show"
      :timeout="snackbar.timeout"
      :color="snackbar.color"
      top
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn v-bind="attrs" text @click="snackbar.show = false">關閉</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { formatNumber } from "@/utils/formatters";

import ConfirmDialog from "@/components/Common/ConfirmDialog.vue";

export default {
  name: "AttendancePreview",
  components: {
    ConfirmDialog,
  },
  props: {},
  data() {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    return {
      loading: false,
      showProgressDialog: false,
      progressMessage: "",
      salaries: [],
      page: 1,
      pageCount: 1,
      itemsPerPage: 15,
      totalItems: 0,
      loadingMore: false,
      fetchError: false,
      observer: null,
      selectedYear: currentYear,
      selectedMonth: currentMonth,
      yearOptions: Array.from({ length: 10 }, (_, i) => currentYear - 5 + i),
      monthOptions: Array.from({ length: 12 }, (_, i) => i + 1),
      exportType: "attendance",

      exporting: false,

      editingId: null,
      editedItem: {},
      saving: false,

      tempInsurance: {
        employer_labor_insurance: 0,
        employer_health_insurance: 0,
        employer_labor_pension: 0,
        employer_occupational_accident_insurance: 0,
        employer_employment_insurance: 0,
        employee_labor_insurance: 0,
        employee_health_insurance: 0,
        employee_welfare_fund: 0,
        employee_voluntary_pension: false,
        employee_voluntary_pension_rate: 6,
        extra_insurance_items: [],
      },

      confirmDialog: {
        show: false,
        title: "",
        message: "",
        color: "primary",
        confirmText: "確認",
        onConfirm: () => {},
      },
      snackbar: {
        show: false,
        text: "",
        color: "success",
        timeout: 3000,
      },
      headers: [
        {
          text: "姓名",
          sortable: false,
          value: "user.name",
          class: "text-secondary font-weight-bolder opacity-7 black--text",
          width: "100px",
        },
        // 任職日期
        {
          text: "任職日期",
          value: "expected_sign_date",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
          width: "80px",
        },
        {
          text: "特休",
          value: "annual_leave",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
        {
          text: "事假",
          value: "personal_leave",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
        {
          text: "病假",
          value: "sick_leave",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },

        {
          text: "生理假",
          value: "menstrual_leave",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
        {
          text: "家庭照顧假",
          value: "family_care_leave",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
        {
          text: "遲到",
          value: "late_info",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
          width: "130px",
        },
        {
          text: "早退",
          value: "early_info",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
          width: "130px",
        },
        {
          text: "曠職",
          value: "absence_info",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
          width: "130px",
        },

        //實發

        {
          text: "操作",
          value: "actions",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
          width: "150px",
        },
      ],
    };
  },
  computed: {
    ...mapGetters("user", ["userToken"]),
    ...mapGetters("company", ["companies", "selectedCompany"]),

    company_id() {
      return this.selectedCompany ? this.selectedCompany.id : null;
    },
    hasMore() {
      return this.page < this.pageCount;
    },
    exportDisabled() {
      return (
        !this.company_id ||
        !this.selectedYear ||
        !this.selectedMonth ||
        this.exporting
      );
    },
  },
  watch: {
    company_id: {
      handler(newVal) {
        if (newVal) {
          this.fetchSalaries();
        }
      },
      immediate: true,
    },
  },

  async mounted() {
    try {
      this.$root.$on("company-changed", this.handleCompanyChanged);
      this.$nextTick(() => {
        this.initIntersectionObserver();
      });
    } catch (error) {
      console.error("Error in mounted:", error);
      this.showSnackbar("Failed to initialize component", "error");
    }
  },
  beforeDestroy() {
    this.$root.$off("company-changed", this.handleCompanyChanged);
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    formatROCDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "-";
      const year = date.getFullYear() - 1911;
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}/${month}/${day}`;
    },
    isNewEmployee(item) {
      if (
        item.user &&
        item.user.employeeProfile &&
        item.user.employeeProfile.expected_sign_date
      ) {
        const signDate = new Date(item.user.employeeProfile.expected_sign_date);
        const selectedYear = parseInt(this.selectedYear);
        const selectedMonth = parseInt(this.selectedMonth);
        return (
          signDate.getFullYear() === selectedYear &&
          signDate.getMonth() + 1 === selectedMonth
        );
      }
      return false;
    },
    rowClasses(item) {
      if (this.isNewEmployee(item)) {
        return "light-blue lighten-5";
      }
      return "";
    },
    goToUserProfile(userId) {
      if (!userId) return;
      this.$router.push(`/pages/users/profile/${userId}`);
    },
    initIntersectionObserver() {
      if (this.observer) {
        this.observer.disconnect();
      }

      const options = {
        root: null,
        rootMargin: "0px",
        threshold: 0.1,
      };

      this.observer = new IntersectionObserver((entries) => {
        const target = entries[0];
        if (
          target.isIntersecting &&
          !this.loading &&
          !this.loadingMore &&
          !this.fetchError &&
          this.hasMore
        ) {
          console.log("fetchSalaries(0, true)");
          this.fetchSalaries(0, true);
        }
      }, options);

      const target = document.querySelector("#sentinel-salary-preview");
      if (target) {
        this.observer.observe(target);
      }
    },
    showSnackbar(text, type = "success") {
      this.snackbar.text = text;
      this.snackbar.color = type === "error" ? "error" : "success";
      this.snackbar.show = true;
    },
    validateExportType() {
      const allowed =
        this.allowedExportTypes && this.allowedExportTypes.length > 0
          ? this.allowedExportTypes
          : this.exportOptions.map((o) => o.value);

      if (!allowed.includes(this.exportType)) {
        this.exportType = allowed[0];
      }
    },

    parseDownloadFilename(contentDisposition, fallbackFilename) {
      if (!contentDisposition) return fallbackFilename;

      const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
      if (utf8Match && utf8Match[1]) {
        try {
          return decodeURIComponent(utf8Match[1].replace(/"/g, ""));
        } catch (e) {
          return utf8Match[1].replace(/"/g, "");
        }
      }

      const match = contentDisposition.match(/filename=([^;]+)/i);
      if (match && match[1]) {
        return match[1].trim().replace(/^"|"$/g, "");
      }

      return fallbackFilename;
    },
    async exportMonthlySummary(user_id = null, name = null) {
      if (this.exportDisabled) return;

      // Check if any employee is recently joined (joined in the selected month)
      // const hasNewEmployee =  this.salaries.some((item) => {
      //   if (
      //     item.user &&
      //     item.user.employeeProfile &&
      //     item.user.employeeProfile.expected_sign_date
      //   ) {
      //     const signDate = new Date(
      //       item.user.employeeProfile.expected_sign_date,
      //     );
      //     const selectedYear = parseInt(this.selectedYear);
      //     const selectedMonth = parseInt(this.selectedMonth);

      //     return (
      //       signDate.getFullYear() === selectedYear &&
      //       signDate.getMonth() + 1 === selectedMonth
      //     );
      //   }
      //   return false;
      // });
      const hasNewEmployee = false;

      if (hasNewEmployee) {
        this.confirmDialog = {
          show: true,
          title: "匯出確認",
          message:
            "偵測到本月有新進員工（未滿一個月），請確認是否已調整勞健保金額？",
          color: "warning",
          confirmText: "確認已調整並匯出",
          noButtonText: "取消",
          onConfirm: async () => {
            this.confirmDialog.show = false;
            await this.executeExport(user_id, name);
          },
        };
        return;
      }
      await this.executeExport(user_id, name);
    },

    async executeExport(user_id = null, name = null) {
      try {
        this.exporting = true;
        this.showProgressDialog = true;
        this.progressMessage = "正在匯出 Excel...";

        let queryParams = `year=${this.selectedYear}&month=${this.selectedMonth}&company_id=${this.company_id}&export=attendance`;
        if (user_id) {
          queryParams += `&user_id=${user_id}`;
        }

        const response = await this.$axios.get(
          `/api/summary/monthly-summary?${queryParams}`,
          { responseType: "blob" }
        );

        const prefix = "出勤紀錄表";
        const companyName =
          this.selectedCompany?.company_name || `company-${this.company_id}`;
        const filenameParts = [
          prefix,
          this.selectedYear,
          this.selectedMonth,
          companyName,
        ];
        if (user_id && name) {
          filenameParts.push(name);
        }
        const defaultFilename = `${filenameParts.join("-")}.xlsx`;
        // const filename = this.parseDownloadFilename(
        //   response.headers?.["content-disposition"],
        //   defaultFilename
        // );
        const filename = defaultFilename;
        console.log("exportType", this.exportType);

        const blob = new Blob([response.data], {
          type:
            response.headers?.["content-type"] ||
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);

        this.showSnackbar("匯出成功", "success");
      } catch (error) {
        console.error("Error exporting monthly summary:", error);
        const errorMsg =
          error.response?.data?.message || error.message || "匯出失敗";
        this.showSnackbar(`匯出失敗：${errorMsg}`, "error");
      } finally {
        this.exporting = false;
        setTimeout(() => {
          this.showProgressDialog = false;
        }, 200);
      }
    },
    async fetchSalaries(reset = 0, append = false) {
      try {
        // if not this.company_id, return
        if (!this.company_id) {
          return;
        }
        if (append) {
          if (!this.hasMore) return;
          this.loadingMore = true;
          this.page += 1;
        } else {
          this.loading = true;
          this.showProgressDialog = true;
          this.progressMessage = "載入薪資資料...";
          this.page = 1;
          this.salaries = [];
        }
        this.fetchError = false;

        let queryParams = `page=${this.page}&pageSize=${this.itemsPerPage}`;

        if (this.selectedYear) {
          queryParams += `&year=${this.selectedYear}`;
        }

        if (this.selectedMonth) {
          queryParams += `&month=${this.selectedMonth}`;
        }

        if (this.company_id) {
          queryParams += `&company_id=${this.company_id}`;
        }

        let effectiveReset = reset;
        const selectedYear = Number(this.selectedYear);
        const selectedMonth = Number(this.selectedMonth);
        if (
          Number.isFinite(selectedYear) &&
          Number.isFinite(selectedMonth)
        ) {
          const now = new Date();
          const currentYear = now.getFullYear();
          const currentMonth = now.getMonth() + 1;
          const isCurrentOrFuture =
            selectedYear > currentYear ||
            (selectedYear === currentYear && selectedMonth >= currentMonth);
          if (isCurrentOrFuture && Number(effectiveReset) !== 2) {
            effectiveReset = 1;
          }
        }

        //add reset parameter to force reload
        queryParams += `&reset=${effectiveReset}`;
        // queryParams += `&reset=1`;

        const response = await this.$axios.get(
          `/api/salaries/preview?${queryParams}`
        );

        const newItems = response.data.data.salaries || [];
        const pagination = response.data.data.pagination;
        this.pageCount = pagination.totalPages;
        this.totalItems = pagination.total;

        if (append) {
          this.salaries = [...this.salaries, ...newItems];
        } else {
          this.salaries = newItems;
        }
      } catch (error) {
        if (append && this.page > 1) {
          this.page -= 1;
        }
        console.error("Error fetching salaries:", error);
        this.fetchError = true;
        this.progressMessage = "載入資料失敗...";
        this.showSnackbar(this.progressMessage, "error");
      } finally {
        this.loading = false;
        this.loadingMore = false;
        setTimeout(() => {
          this.showProgressDialog = false;
        }, 200);
      }
    },
    formatNonWageSalary(data) {
      if (!data || typeof data !== "object" || Object.keys(data).length === 0)
        return "-";
      return Object.entries(data)
        .map(([k, v]) => `${k}:${Number(v).toLocaleString()}`)
        .join("，");
    },
    openNonWageSalaryDialog() {
      const data = this.editedItem.non_wage_salary;
      this.nonWageSalaryItems =
        data && typeof data === "object"
          ? Object.keys(data).map((k) => ({
              key: k,
              value: Number(data[k]) || 0,
            }))
          : [];
      this.nonWageSalaryDialog = true;
    },
    closeNonWageSalaryDialog() {
      this.nonWageSalaryDialog = false;
      this.nonWageSalaryItems = [];
    },
    addNonWageSalaryItem() {
      this.nonWageSalaryItems.push({ key: "", value: 0 });
    },
    removeNonWageSalaryItem(idx) {
      this.nonWageSalaryItems.splice(idx, 1);
    },
    confirmNonWageSalaryDialog() {
      const map = {};
      this.nonWageSalaryItems
        .filter(
          (it) =>
            (it?.key || "").toString().trim() !== "" ||
            (Number(it?.value) || 0) > 0
        )
        .forEach((it) => {
          const k = (it?.key || "").toString().trim() || "未命名";
          const v = Number(it?.value) || 0;
          map[k] = v;
        });
      this.editedItem.non_wage_salary = map;
      this.closeNonWageSalaryDialog();
    },
    formatCompanyExtraSalaryString(data) {
      if (!data || typeof data !== "object" || Object.keys(data).length === 0)
        return "未設定";
      const entries = Object.entries(data);

      // If only one item, show the detail
      if (entries.length === 1) {
        const [key, value] = entries[0];
        const val = Number(value) || 0;
        return `${key}:${val.toLocaleString()}`;
      }

      // If more than one item, show summary: "共X筆 $總金額"
      const total = entries.reduce((sum, [, value]) => {
        return sum + (Number(value) || 0);
      }, 0);
      return `共${entries.length}筆 $${total.toLocaleString()}`;
    },
    openCompanyExtraSalaryDialog() {
      const data = this.editedItem.company_extra_salary;

      this.companyExtraSalaryItems =
        data && typeof data === "object"
          ? Object.keys(data).map((k) => ({
              key: k,
              value: Number(data[k]) || 0,
            }))
          : [];
      this.companyExtraSalaryDialog = true;
    },
    closeCompanyExtraSalaryDialog() {
      this.companyExtraSalaryDialog = false;
      this.companyExtraSalaryItems = [];
    },
    addCompanyExtraSalaryItem() {
      this.companyExtraSalaryItems.push({ key: "", value: 0 });
    },
    removeCompanyExtraSalaryItem(idx) {
      this.companyExtraSalaryItems.splice(idx, 1);
    },
    confirmCompanyExtraSalaryDialog() {
      const map = {};
      this.companyExtraSalaryItems
        .filter(
          (it) =>
            (it?.key || "").toString().trim() !== "" ||
            (Number(it?.value) || 0) > 0
        )
        .forEach((it) => {
          const k = (it?.key || "").toString().trim() || "未命名";
          const v = Number(it?.value) || 0;
          map[k] = v;
        });
      this.editedItem.company_extra_salary = map;
      this.closeCompanyExtraSalaryDialog();
    },

    openInsuranceDialog() {
      this.tempInsurance = {
        employer_labor_insurance: this.editedItem.employer_labor_insurance || 0,
        employer_health_insurance:
          this.editedItem.employer_health_insurance || 0,
        employer_labor_pension: this.editedItem.employer_labor_pension || 0,
        employer_occupational_accident_insurance:
          this.editedItem.employer_occupational_accident_insurance || 0,
        employer_employment_insurance:
          this.editedItem.employer_employment_insurance || 0,

        employee_labor_insurance: this.editedItem.employee_labor_insurance || 0,
        employee_health_insurance:
          this.editedItem.employee_health_insurance || 0,
        employee_welfare_fund: this.editedItem.employee_welfare_fund || 0,
        employee_voluntary_pension:
          this.editedItem.employee_voluntary_pension || false,
        employee_voluntary_pension_rate:
          this.editedItem.employee_voluntary_pension_rate || 6,
        extra_insurance_items: [],
      };

      // Handle extra_insurance parsing
      let extraInsuranceData = {};
      if (this.editedItem.extra_insurance) {
        try {
          extraInsuranceData =
            typeof this.editedItem.extra_insurance === "string"
              ? JSON.parse(this.editedItem.extra_insurance)
              : JSON.parse(JSON.stringify(this.editedItem.extra_insurance));
        } catch (e) {
          console.error("Failed to parse extra_insurance:", e);
        }
      }

      const migratedExtraInsurance = {
        ...(extraInsuranceData && typeof extraInsuranceData === "object"
          ? extraInsuranceData
          : {}),
      };

      // Normalize old format if exists
      if (
        Array.isArray(migratedExtraInsurance.dependent_health_insurance_items)
      ) {
        migratedExtraInsurance.dependent_health_insurance_items.forEach(
          (it) => {
            const k = (it?.key || "").toString().trim() || "團體保險";
            const v = Number(it?.value) || 0;
            if (v > 0) migratedExtraInsurance[k] = v;
          }
        );
      }
      if (migratedExtraInsurance.dependent_health_insurance) {
        const v =
          Number(migratedExtraInsurance.dependent_health_insurance) || 0;
        if (v > 0) migratedExtraInsurance["團體保險"] = v;
      }
      // Remove known non-extra fields from this object to avoid duplication if they were stored there
      delete migratedExtraInsurance.employee_voluntary_pension;
      delete migratedExtraInsurance.dependent_health_insurance;
      delete migratedExtraInsurance.dependent_health_insurance_items;

      this.tempInsurance.extra_insurance_items = Object.keys(
        migratedExtraInsurance
      ).map((k) => ({
        key: k,
        value: Number(migratedExtraInsurance[k]) || 0,
      }));

      this.insuranceDialog = true;
    },
    confirmInsuranceDialog() {
      this.editedItem.employer_labor_insurance =
        Number(this.tempInsurance.employer_labor_insurance) || 0;
      this.editedItem.employer_health_insurance =
        Number(this.tempInsurance.employer_health_insurance) || 0;
      this.editedItem.employer_labor_pension =
        Number(this.tempInsurance.employer_labor_pension) || 0;
      this.editedItem.employer_occupational_accident_insurance =
        Number(this.tempInsurance.employer_occupational_accident_insurance) ||
        0;
      this.editedItem.employer_employment_insurance =
        Number(this.tempInsurance.employer_employment_insurance) || 0;

      this.editedItem.employee_labor_insurance =
        Number(this.tempInsurance.employee_labor_insurance) || 0;
      this.editedItem.employee_health_insurance =
        Number(this.tempInsurance.employee_health_insurance) || 0;
      this.editedItem.employee_welfare_fund =
        Number(this.tempInsurance.employee_welfare_fund) || 0;
      this.editedItem.employee_voluntary_pension =
        this.tempInsurance.employee_voluntary_pension;
      this.editedItem.employee_voluntary_pension_rate =
        Number(this.tempInsurance.employee_voluntary_pension_rate) || 6;

      // Save extra_insurance
      const map = {};
      this.tempInsurance.extra_insurance_items
        .filter(
          (it) =>
            (it?.key || "").toString().trim() !== "" ||
            (Number(it?.value) || 0) > 0
        )
        .forEach((it) => {
          const k = (it?.key || "").toString().trim() || "團體保險";
          const v = Number(it?.value) || 0;
          map[k] = v;
        });
      this.editedItem.extra_insurance = map;

      this.insuranceDialog = false;
    },
    addExtraInsuranceItem() {
      this.tempInsurance.extra_insurance_items.push({
        key: "團體保險",
        value: 0,
      });
    },
    removeExtraInsuranceItem(idx) {
      this.tempInsurance.extra_insurance_items.splice(idx, 1);
    },
    onFilterChange() {
      this.page = 1;
      this.fetchSalaries(0, false);
      console.log("onFilterChange");
    },
    async handleCompanyChanged(company) {
      if (company && company.id !== this.company_id) {
        this.page = 1;
        await this.fetchSalaries(0, false);
        console.log("handleCompanyChanged");
      }
    },
    formatCurrency(value) {
      if (value === null || value === undefined) {
        return "-";
      }
      return formatNumber(value);
    },
    editItem(item) {
      this.editingId = item.id;
      this.editedItem = {
        basic_salary: item.basic_salary,
        supervisor_allowance: item.supervisor_allowance,
        position_allowance: item.position_allowance,
        food_allowance: item.food_allowance,
        bonus: item.bonus,
        attendance_bonus: item.attendance_bonus,
        other_earnings: item.other_earnings,
        salary_avg: item.salary_avg,
        bracket_max: item.bracket_max,
        work_0_2: item.work_0_2,
        work_2_4: item.work_2_4,
        rest_0_2: item.rest_0_2,
        rest_2_8: item.rest_2_8,
        rest_8_12: item.rest_8_12,
        nat_0_8: item.nat_0_8,
        nat_9_10: item.nat_9_10,
        nat_11_12: item.nat_11_12,
        personal_leave: item.personal_leave,
        sick_leave: item.sick_leave,
        annual_leave: item.annual_leave,
        menstrual_leave: item.menstrual_leave,
        family_care_leave: item.family_care_leave,
        extra_leave: item.extra_leave,
        late: item.late,
        late_count: item.late_count,
        early: item.early,
        early_count: item.early_count,
        absence_count: item.absence_count,
        absence_minutes: item.absence_minutes,
        ignore_late_early: !!item.extra?.ignore_late_early,
        other_deductions: item.other_deductions,
        employer_labor_insurance: item.employer_labor_insurance,
        employer_health_insurance: item.employer_health_insurance,
        employer_labor_pension: item.employer_labor_pension,
        employer_occupational_accident_insurance:
          item.employer_occupational_accident_insurance,
        employer_employment_insurance: item.employer_employment_insurance,
        employee_labor_insurance: item.employee_labor_insurance,
        employee_health_insurance: item.employee_health_insurance,
        employee_welfare_fund: item.employee_welfare_fund,
        employee_voluntary_pension: item.employee_voluntary_pension,
        employee_voluntary_pension_rate: item.employee_voluntary_pension_rate,
        company_extra_salary:
          item.company_extra_salary &&
          typeof item.company_extra_salary === "object"
            ? JSON.parse(JSON.stringify(item.company_extra_salary))
            : {},
        extra_insurance:
          item.extra_insurance && typeof item.extra_insurance === "object"
            ? JSON.parse(JSON.stringify(item.extra_insurance))
            : {},
        non_wage_salary:
          item.non_wage_salary && typeof item.non_wage_salary === "object"
            ? JSON.parse(JSON.stringify(item.non_wage_salary))
            : {},
      };
    },
    cancelEdit() {
      this.editingId = null;
      this.editedItem = {};
    },
    async saveEdit(item) {
      try {
        this.saving = true;
        this.showProgressDialog = true;
        this.progressMessage = "儲存薪資資料...";

        const updateData = {};
        Object.keys(this.editedItem).forEach((key) => {
          if (
            this.editedItem[key] !== null &&
            this.editedItem[key] !== undefined
          ) {
            updateData[key] = this.editedItem[key];
          }
        });

        await this.$axios.put(`/api/salaries/${item.id}`, updateData);

        await this.fetchSalaries(0);

        this.showSnackbar("薪資資料已更新", "success");
        this.editingId = null;
        this.editedItem = {};
      } catch (error) {
        console.error("Error updating salary:", error);
        const errorMsg =
          error.response?.data?.message || error.message || "更新失敗";
        this.showSnackbar(`更新薪資失敗：${errorMsg}`, "error");
      } finally {
        this.saving = false;
        setTimeout(() => {
          this.showProgressDialog = false;
        }, 200);
      }
    },
    async resetItem(item) {
      this.confirmDialog = {
        show: true,
        title: "重置確認",
        message: `確定要重置 ${
          item.user?.name || ""
        } 的薪資資料嗎？這將重新計算並覆蓋手動修改的內容。`,
        color: "error",
        confirmText: "重置",
        noButtonText: "取消",
        onConfirm: async () => {
          this.confirmDialog.show = false;
          try {
            this.showProgressDialog = true;
            this.progressMessage = "正在重置薪資資料...";

            const response = await this.$axios.post(
              `/api/salaries/${item.id}/reset?reset=2`
            );

            const updatedSalary = response.data.data.salary;
            const index = this.salaries.findIndex((s) => s.id === item.id);
            if (index !== -1) {
              this.$set(this.salaries, index, {
                ...this.salaries[index],
                ...updatedSalary,
              });
            }

            this.showSnackbar("薪資資料已重置", "success");
          } catch (error) {
            console.error("Error resetting salary:", error);
            const errorMsg =
              error.response?.data?.message || error.message || "重置失敗";
            this.showSnackbar(`重置失敗：${errorMsg}`, "error");
          } finally {
            setTimeout(() => {
              this.showProgressDialog = false;
            }, 200);
          }
        },
      };
    },
    confirmResetAll() {
      const year = this.selectedYear;
      const month = this.selectedMonth;
      const companyName = this.selectedCompany?.company_name || "";

      this.confirmDialog = {
        show: true,
        title: "全部重設確認",
        message: `確定要重置 ${companyName} ${year}年${month}月 的所有薪資資料嗎？這將重新計算本月所有員工資料並覆蓋任何手動修改。`,
        color: "error",
        confirmText: "全部重設",
        noButtonText: "取消",
        onConfirm: () => {
          this.confirmDialog.show = false;
          this.fetchSalaries(2);
        },
      };
    },
  },
};
</script>

<style scoped>
.table >>> thead th {
  white-space: nowrap;
  font-size: 1rem !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.025rem;
  color: #8392ab;
  opacity: 0.7;
  padding: 0.75rem 1.5rem;
  border-bottom: 0.0625rem solid #e9ecef;
}

.table >>> tbody tr:last-child td {
  border-bottom: 0;
}

.table >>> tbody td {
  padding: 0.75rem 1.5rem;
  border-bottom: 0.0625rem solid #e9ecef;
}

.table >>> tbody tr:hover {
  background: #f8f9fa;
}

.input-style {
  background-color: #f8f9fa;
  border-radius: 0.5rem;
}

.font-size-input {
  font-size: 0.875rem;
}

.text-light-input {
  color: #495057;
}

.placeholder-light::placeholder {
  color: #adb5bd;
}

.card-shadow {
  box-shadow: 0 20px 27px 0 rgba(0, 0, 0, 0.05);
}

.border-radius-xl {
  border-radius: 1rem;
}

.edit-input {
  max-width: 120px;
}

.edit-input >>> .v-input__control {
  min-height: 32px;
}

.edit-input >>> input {
  font-size: 0.875rem;
}
</style>
